import React, { useState, useMemo } from "react";
import useSWR, { mutate } from "swr";
import { api } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Trash, CaretLeft, CaretRight } from "@phosphor-icons/react";
import { toast } from "sonner";

const fetcher = (url) => api.get(url).then((r) => r.data);

export default function CalendarPage() {
  const { data: events = [] } = useSWR("/events", fetcher);
  const { data: projects = [] } = useSWR("/projects", fetcher);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(() => new Date());
  const [pickedDate, setPickedDate] = useState("");

  const [form, setForm] = useState({
    project_id: "", title: "", crew: "Crew A", date: new Date().toISOString().slice(0,10),
    start_time: "08:00", end_time: "17:00", notes: ""
  });

  const submit = async () => {
    if (!form.title || !form.date) return toast.error("Title and date required");
    try {
      await api.post("/events", { ...form, project_id: form.project_id || null });
      toast.success("Scheduled");
      setOpen(false);
      mutate("/events");
    } catch { toast.error("Failed"); }
  };

  const remove = async (id) => {
    await api.delete(`/events/${id}`);
    mutate("/events");
  };

  // Build calendar grid
  const { cells, monthLabel } = useMemo(() => {
    const y = view.getFullYear(); const m = view.getMonth();
    const first = new Date(y, m, 1);
    const startDay = first.getDay(); // 0 Sun
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(y, m, d));
    while (cells.length % 7 !== 0) cells.push(null);
    const monthLabel = view.toLocaleString(undefined, { month: "long", year: "numeric" });
    return { cells, monthLabel };
  }, [view]);

  const eventsByDate = useMemo(() => {
    const map = {};
    events.forEach(e => { (map[e.date] ||= []).push(e); });
    return map;
  }, [events]);

  const isoDay = (d) => d ? d.toISOString().slice(0, 10) : "";

  return (
    <div data-testid="calendar-page">
      <PageHeader
        eyebrow="// 07 / Calendar"
        title={monthLabel}
        sub="Crew schedule. Click a day to add a job."
        action={
          <div className="flex items-center gap-2">
            <Button variant="outline" data-testid="cal-prev" onClick={() => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1))} className="rounded-none border-zinc-300 h-11 w-11 p-0">
              <CaretLeft size={16} />
            </Button>
            <Button variant="outline" data-testid="cal-today" onClick={() => setView(new Date())} className="rounded-none border-zinc-300 uppercase font-bold tracking-wider text-xs h-11 px-4">Today</Button>
            <Button variant="outline" data-testid="cal-next" onClick={() => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1))} className="rounded-none border-zinc-300 h-11 w-11 p-0">
              <CaretRight size={16} />
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="new-event-button" onClick={() => setForm({ ...form, date: pickedDate || form.date })}
                  className="rounded-none bg-zinc-900 hover:bg-orange-600 text-white uppercase font-bold tracking-wider text-xs h-11 px-5">
                  <Plus size={14} className="mr-2" /> New Event
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-none border-zinc-300 max-w-xl">
                <DialogHeader><DialogTitle className="font-display font-black text-2xl tracking-tight">New Event</DialogTitle></DialogHeader>
                <div className="space-y-3 mt-4">
                  <Field label="Title"><Input data-testid="evt-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-none" /></Field>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Date"><Input data-testid="evt-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-none mono" /></Field>
                    <Field label="Crew"><Input data-testid="evt-crew" value={form.crew} onChange={(e) => setForm({ ...form, crew: e.target.value })} className="rounded-none" /></Field>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Start"><Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="rounded-none mono" /></Field>
                    <Field label="End"><Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="rounded-none mono" /></Field>
                  </div>
                  <Field label="Project (optional)">
                    <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                      <SelectTrigger className="rounded-none"><SelectValue placeholder="None" /></SelectTrigger>
                      <SelectContent>{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label="Notes"><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-none" /></Field>
                  <Button data-testid="evt-save" onClick={submit} className="rounded-none bg-orange-600 hover:bg-orange-700 text-white uppercase font-bold tracking-wider w-full h-11">Schedule</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        }
      />
      <PageBody>
        <div className="border border-zinc-200">
          <div className="grid grid-cols-7 border-b border-zinc-200">
            {["SUN","MON","TUE","WED","THU","FRI","SAT"].map(d => (
              <div key={d} className="label-eyebrow text-zinc-500 px-3 py-3 border-r border-zinc-200 last:border-r-0">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {cells.map((d, i) => {
              const iso = isoDay(d);
              const list = (iso && eventsByDate[iso]) || [];
              const isToday = iso === new Date().toISOString().slice(0,10);
              return (
                <button
                  key={i}
                  onClick={() => { if (d) { setPickedDate(iso); setForm({ ...form, date: iso }); setOpen(true); } }}
                  className={`text-left min-h-[110px] border-r border-b border-zinc-100 p-2 transition-colors ${d ? "bg-white hover:bg-zinc-50" : "bg-zinc-50/50"} ${isToday ? "bg-orange-50/40" : ""}`}
                >
                  {d && (
                    <>
                      <div className={`mono text-xs ${isToday ? "text-orange-600 font-bold" : "text-zinc-500"}`}>{d.getDate()}</div>
                      <div className="mt-1 space-y-1">
                        {list.slice(0,3).map(e => (
                          <div key={e.id} className="text-[10px] bg-zinc-900 text-white px-1.5 py-0.5 truncate">
                            {e.start_time} {e.title}
                          </div>
                        ))}
                        {list.length > 3 && <div className="text-[10px] text-zinc-500">+{list.length - 3} more</div>}
                      </div>
                    </>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {events.length > 0 && (
          <div className="mt-8 border border-zinc-200">
            <div className="border-b border-zinc-200 px-5 py-3"><p className="label-eyebrow text-zinc-500">All events</p></div>
            <div>
              {events.map(e => (
                <div key={e.id} className="grid grid-cols-12 px-5 py-3 border-b border-zinc-100 items-center text-sm">
                  <div className="col-span-2 mono text-xs">{e.date}</div>
                  <div className="col-span-2 mono text-xs">{e.start_time}–{e.end_time}</div>
                  <div className="col-span-4 font-semibold">{e.title}</div>
                  <div className="col-span-3 label-eyebrow text-zinc-500">{e.crew}</div>
                  <div className="col-span-1 text-right">
                    <button onClick={() => remove(e.id)} className="text-zinc-400 hover:text-orange-600"><Trash size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </PageBody>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="label-eyebrow text-zinc-600 mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}
